/**
 * Chamadas HTTP da agenda de contatos.
 *
 * Papel: aqui ficam as cinco chamadas de `/api/mailing` que a tela usa — a
 * listagem com busca, o catálogo de categorias, e a criação, a alteração e a
 * exclusão de um contato.
 *
 * Como usar: `criarContato` e `atualizarContato` recebem a lista inteira de
 * telefones, e é ela que passa a valer; enviar só o número alterado apaga os
 * demais. Nenhuma transformação acontece neste arquivo — o que sai é o que o
 * schema validou —, de modo que uma regra de tela colocada aqui deixaria de
 * valer para quem chamasse o service de outro lugar.
 */
import {
  type ContatoMailing,
  type NovoContatoMailing,
  type ParametrosListagem,
  categoriasMailingSchema,
  contatoMailingSchema,
  respostaPaginadaSchema,
} from '@concierge/shared';

import { clienteApi } from '@/shared/api';

/** Envelope paginado da listagem de contatos. */
const listaDeContatosSchema = respostaPaginadaSchema(contatoMailingSchema);

/**
 * Busca uma página da agenda.
 *
 * A busca procura no nome, na categoria, no e-mail, no endereço, na observação,
 * em quem atende cada telefone e no próprio número — e ignora acento e
 * pontuação, de modo que `cobioles` encontre `Cobióles` e `(11) 4195` encontre
 * o número gravado sem pontuação.
 *
 * @param parametros - `pagina` a partir de 1, `porPagina` até 200 e `busca`
 *   opcional; `busca` vazia ou ausente lista todos.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query, para a
 *   página abandonada não sobrescrever a que a pessoa acabou de pedir.
 * @returns A página, no formato de `respostaPaginadaSchema(contatoMailingSchema)`.
 */
export function buscarContatos(parametros: ParametrosListagem, sinal?: AbortSignal) {
  return clienteApi.get('/mailing', {
    schema: listaDeContatosSchema,
    params: parametros,
    sinal,
  });
}

/**
 * Lê as categorias que já estão em uso.
 *
 * Alimenta a lista de sugestão do formulário. É o que impede `Tapeceiro` e
 * `Tapecaria` de passarem a conviver como dois rótulos sem que ninguém note, já
 * que a categoria é texto livre e não uma escolha entre valores fixos.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As categorias distintas, em ordem alfabética que ignora acento.
 */
export function buscarCategorias(sinal?: AbortSignal) {
  return clienteApi.get('/mailing/categorias', { schema: categoriasMailingSchema, sinal });
}

/**
 * Cria um contato com os telefones que vierem junto.
 *
 * @param dados - O contato preenchido no formulário, já validado por
 *   `novoContatoMailingSchema`.
 * @returns O contato como ficou gravado, com identificador e carimbos.
 */
export function criarContato(dados: NovoContatoMailing): Promise<ContatoMailing> {
  return clienteApi.post('/mailing', dados, { schema: contatoMailingSchema });
}

/**
 * Substitui todos os campos de um contato e o conjunto de telefones dele.
 *
 * @param id - Identificador da linha em `contatos_mailing`.
 * @param dados - O contato inteiro, e não apenas os campos alterados.
 * @returns O contato como ficou gravado.
 */
export function atualizarContato(
  id: string,
  dados: NovoContatoMailing,
): Promise<ContatoMailing> {
  return clienteApi.put(`/mailing/${encodeURIComponent(id)}`, dados, {
    schema: contatoMailingSchema,
  });
}

/**
 * Apaga um contato e, junto com ele, os telefones dele.
 *
 * Não recebe schema porque a API responde 204 sem corpo, e o parse falharia
 * justamente no caminho de sucesso.
 *
 * @param id - Identificador da linha em `contatos_mailing`.
 */
export function excluirContato(id: string): Promise<void> {
  return clienteApi.remover(`/mailing/${encodeURIComponent(id)}`);
}
